/**
 * Home Option B — compact spine.
 *
 * Single-viewport home rebuild (v1.1.3-b5). Replaces the long "/" profile page
 * with a compact spine layout: neon-green vertical bar on the left, KPI strip
 * (4 chips), next-action card, recent activity, primary CTA.
 *
 * Data sources preserved from the prior home:
 *   - Wave 0.5 KPIs: live counts from /api/dex/batches.stats
 *   - Wave 3 bell: provided by AppShell header
 *   - Recent activity: the last few confirmed catches (V2SilhouetteCard strip)
 *   - Next action: Live Scan via openLiveScan() or /catch if dex already
 *     imported and recent
 *
 * The fat trainer surfaces (friend code, trainer strategy, inventory hub,
 * affiliate items, etc.) moved off the home; they remain reachable via the
 * gear icon on the hero row (routes to /settings, see PR #26) and dedicated
 * routes (/inventory, /trade, /battle, /calendar).
 *
 * v1.1.3-b6 (PR #27 resolved): adds the settings gear icon back into the
 * hero row + re-mounts the J7.5 HundoDexSection between Recent Activity
 * and Up Next so trainers still see their hundo trail on Home.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { AppShell } from "@/components/AppShell";
import { MenuHeading } from "@/components/MenuHeading";
import { InfoBlockCollapseContext, type InfoBlockCollapse } from "@/components/InfoBlock";
import {
  isProfileBlockOpen,
  setProfileBlockOpen,
  setProfileBlocksOpen,
  useProfileBlockPrefs,
} from "@/lib/profileBlockPrefs";
import { primeBootPermissions } from "@/lib/permissionPrime";
import { useFirstRunPromptDue } from "@/lib/useFirstRunPrompt";
import { DEX_TOTALS_EVENT } from "@/lib/dexQueryRefresh";
import { DEX_STATE_EVENT } from "@/lib/dexStateStore";
import { useDexCompletion } from "@/hooks/useDexCompletion";
import { ResumeScanBanner } from "@/components/ResumeScanBanner";
import { HundoDexSection } from "@/components/HundoDexSection";
import { ProfilePriorityWidgets, PvpSeasonPanel } from "@/components/ProfilePriorityWidgets";
import { ShinyHuntBlock } from "@/components/ShinyHuntBlock";
import { WildAreaCard } from "@/components/WildAreaCard";
import { RocketRadarBlock } from "@/components/RocketRadarBlock";
import { RaidBossPanel } from "@/components/RaidBossPanel";
import { HundoRaidCpBlock } from "@/components/HundoRaidCpBlock";
import { EventCatchUpSection } from "@/components/EventCatchUp";
import { PvpEventStrip } from "@/components/PvpEventStrip";
import { ShundoTrophyPanel } from "@/components/ShundoTrophyPanel";
import { useSession } from "@/components/SessionProvider";
import { collectionFocusLabel, focusToDexVariant } from "@/lib/userPriorities";
import { useInventory } from "@/components/InventoryProvider";
import { EVENTS } from "@/lib/data";
import { eventsLiveKey } from "@/lib/eventsLiveKey";
import { armCalendarEvent } from "@/lib/calendarDayScroll";
import { eventStartMs } from "@/lib/eventRecency";
import { type RecentRecordRow } from "@/lib/profileActivity";
import { ChevronRight, Calendar as CalendarIcon, ChevronsDownUp, ChevronsUpDown, Gauge, type LucideIcon } from "lucide-react";
/**
 * The single vertical gap between two blocks on the spine. Every wrapper below
 * uses this and nothing else sets a top margin, so the page cannot drift back
 * into having two rhythms.
 */
const BLOCK_GAP = "mt-5";

export default function Profile() {
  const { userPriorities } = useSession();
  const { stats: invStats } = useInventory();
  const [, setLocation] = useLocation();

  // Collapsible blocks (2026-09-19 owner ask): every InfoBlock on the spine
  // folds to its heading, remembered per block on this device. The blocks
  // register their keys as they mount so the pill at the top can act on
  // exactly what is on screen. `prefs` is subscribed so a toggle re-renders
  // every block through the context value below.
  const prefs = useProfileBlockPrefs();
  const [blockKeys, setBlockKeys] = useState<ReadonlySet<string>>(() => new Set());
  const registryRef = useRef(new Map<string, number>());
  const register = useCallback((key: string) => {
    const m = registryRef.current;
    m.set(key, (m.get(key) ?? 0) + 1);
    setBlockKeys(new Set(m.keys()));
    return () => {
      const n = (m.get(key) ?? 1) - 1;
      if (n <= 0) m.delete(key);
      else m.set(key, n);
      setBlockKeys(new Set(m.keys()));
    };
  }, []);
  const collapse = useMemo<InfoBlockCollapse>(
    () => ({
      isOpen: (key) => isProfileBlockOpen(key),
      toggle: (key) => setProfileBlockOpen(key, !isProfileBlockOpen(key)),
      register,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [register, prefs],
  );
  const anyOpen = [...blockKeys].some((k) => isProfileBlockOpen(k));
  const toggleAll = () => setProfileBlocksOpen(blockKeys, !anyOpen);

  // Warm the My Pokémon tab while the trainer reads their Profile
  // (2026-08-21 device report: first tap on the tab sat on a spinner). Two
  // costs get paid early, off the boot path: the page's lazy JS chunk, and
  // the record store's IndexedDB hydration (listMyPokemon kicks it). Deferred
  // past first paint so it cannot slow the launch it exists to hide.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      void import("@/pages/MyPokemon");
      import("@/lib/myPokemonStore")
        .then((m) => void m.listMyPokemon())
        .catch(() => {});
    }, 1500);
    return () => window.clearTimeout(timer);
  }, []);
  // PR-D3 (2026-06-15): the multi-screen LiveScanModal is gone. The primary
  // scan action navigates directly to the Pokédex page, which pops the
  // DexLiveScanMenu for per-variant ingestion.
  const startScan = () => setLocation("/pokedex");

  // Prime the OS push permission from the home screen, so the trainer sees the
  // system sheet (they reported never being asked). Since 2026-10-23 it waits
  // its turn like the other optional asks (lib/firstRunPrompts): from the
  // third session, and never in a session that already had one.
  const notificationsDue = useFirstRunPromptDue("notifications");
  useEffect(() => {
    if (!notificationsDue) return;
    void primeBootPermissions();
  }, [notificationsDue]);

  // Profile KPI counters share their data sources with the Dex tab so the
  // numbers tick in lock-step with anything that updates the trainer's
  // dex. Main comes from /api/dex/completion (canonical Pokedex read
  // model); shiny / lucky / hundo come from /api/dex/batches.stats
  // (record-level variant counts). Both are invalidated by
  // invalidateDexQueries() after every successful ingest — no isProd
  // gate, no KPIS sample fallback.
  const batchesQ = useQuery<{
    stats?: {
      total?: number;
      totalRecords?: number;
      shinies?: number;
      hundos?: number;
      lucky?: number;
      lastUploadAt?: string | null;
    };
  }>({ queryKey: ["/api/dex/batches"], refetchOnMount: "always" });
  const completionQ = useQuery<{
    ultimateDexCaught?: number;
    ultimateDexTotal?: number;
    percentComplete?: number;
  }>({ queryKey: ["/api/dex/completion"], refetchOnMount: "always" });
  const recordsQ = useQuery<{ rows?: ReadonlyArray<RecentRecordRow> }>({
    queryKey: ["/api/dex/records"],
    // b15b U1: drop the 60s stale window so the recent-activity strip reflects
    // a fresh dex entry the moment a scan/import lands. The DEX_TOTALS_EVENT
    // listener below explicitly refetches, but a stale window let an
    // already-mounted Profile serve cached rows; treat records as always-fresh
    // so the strip is live, matching the Pokédex grid's instant update.
    staleTime: 0,
    refetchOnMount: "always",
  });

  // v1.2.1(9): listen for the DEX_TOTALS_EVENT so the KPI strip refreshes
  // even when the Profile page remained mounted through a LiveScan
  // overlay. React Query invalidate already triggers a refetch, but
  // the explicit refetch() here closes the loop deterministically.
  // b15b U1/U2: also re-read the local dex store (countsTick) and refetch the
  // records query on both DEX_TOTALS_EVENT (server ingest) and DEX_STATE_EVENT
  // (a manual toggle on the Pokédex page) so KPIs + recent activity stay live.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => {
      batchesQ.refetch().catch(() => null);
      completionQ.refetch().catch(() => null);
      recordsQ.refetch().catch(() => null);
    };
    window.addEventListener(DEX_TOTALS_EVENT, handler);
    window.addEventListener(DEX_STATE_EVENT, handler);
    return () => {
      window.removeEventListener(DEX_TOTALS_EVENT, handler);
      window.removeEventListener(DEX_STATE_EVENT, handler);
    };
  }, [batchesQ, completionQ, recordsQ]);

  // The four KPI chips read from the SAME source as the Pokédex page's
  // "Pokémon GO Released" ring — useDexCompletion, whose `inGoCaught` is the
  // caught count INTERSECTED with the released-in-GO roster (present ∩ roster),
  // and `inGoTotal` is that roster's size. Using the raw cascade total here
  // (the previous `deriveDexCounts` path) let a catch sitting OUTSIDE the
  // released roster inflate the headline above the roster — e.g. Main 959 over a
  // 951-species roster, a >100% the old code silently clamped. Intersecting
  // bounds every chip to its roster and guarantees Profile and the Dex page can
  // never disagree again.
  const mainC = useDexCompletion("all");
  const shinyC = useDexCompletion("shiny");
  const luckyC = useDexCompletion("lucky");
  const perfectC = useDexCompletion("perfect");
  const mainCaught = mainC.inGoCaught;
  const shinyCaught = shinyC.inGoCaught;
  const luckyCaught = luckyC.inGoCaught;
  const perfectCaught = perfectC.inGoCaught;
  const mainPct = mainC.inGoPct;
  const shinyPct = shinyC.inGoPct;
  const luckyPct = luckyC.inGoPct;
  const perfectPct = perfectC.inGoPct;

  // Up next: the soonest event still to start, from the LIVE feed (2026-09-04
  // owner report: the card never showed in production). It used to read the
  // bundled EVENTS list, which is EMPTY in production builds by design, so
  // the row only ever appeared in development. The feed's upcoming bucket is
  // what the Calendar shows; the dev seed remains the fallback offline.
  const liveQ = useQuery<{ upcoming?: Array<{ id: string; name: string; start?: string | null }> }>({
    queryKey: [eventsLiveKey()],
    staleTime: 5 * 60_000,
  });
  const upcoming = useMemo(() => {
    const now = Date.now();
    const soonest = (liveQ.data?.upcoming ?? [])
      .map((ev) => ({ ev, startMs: eventStartMs(ev) }))
      .filter((x) => Number.isFinite(x.startMs) && x.startMs > now)
      .sort((a, b) => a.startMs - b.startMs)[0];
    if (soonest) {
      return {
        id: soonest.ev.id,
        title: soonest.ev.name,
        date: new Date(soonest.startMs).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }),
      };
    }
    const today = new Date();
    const seed = EVENTS
      .filter((e) => {
        const s = e.start ? new Date(e.start) : null;
        return !s || s >= today;
      })
      .sort((a, b) => (a.start || "").localeCompare(b.start || ""))[0]
      ?? EVENTS[0];
    return seed ? { id: seed.id, title: seed.title, date: seed.date } : undefined;
  }, [liveQ.data]);

  // Recent-activity projection removed with the strip (2026-07-01). recordsQ
  // is still fetched because it drives the KPI/dex-state refetch loop below.

  // b13d S3: the bottom trophy-dex slot reflects the trainer's top collection
  // focus (Page-3 questionnaire main priority). Hundos keep the canonical
  // "Hundo dex" heading; everything else reads "<Priority> Dex". Falls back to
  // "Hundo dex" before the questionnaire is completed.
  // b18-profile-1: pvp_top and shundo get dedicated custom slots (see
  // dexCustomSlot below), so they fall through to undefined here -- the
  // customSlot prop overrides dexSlotTitle/dexSlotVariant entirely.
  const dexSlotTitle = useMemo(() => {
    if (userPriorities.questionnaireCompletedAt === null) return undefined;
    const top = userPriorities.collectionFoci[0];
    if (!top || top === "hundos" || top === "pvp_top" || top === "shundo") return undefined;
    const label = collectionFocusLabel(top);
    return label.endsWith("Dex") ? label : `${label} Dex`;
  }, [userPriorities.questionnaireCompletedAt, userPriorities.collectionFoci]);

  // b15b U3: when the #1 collection focus maps to a dex variant the local store
  // tracks (shiny / lucky), drive the trophy-dex panel off that variant so its
  // sprite filter + caught/total count reflect the priority (e.g. "Lucky Dex
  // 12/151"). Hundos / multi-tile foci leave this undefined -> rich hundo panel.
  const dexSlotVariant = useMemo(() => {
    if (userPriorities.questionnaireCompletedAt === null) return undefined;
    const top = userPriorities.collectionFoci[0];
    if (!top) return undefined;
    return focusToDexVariant(top) ?? undefined;
  }, [userPriorities.questionnaireCompletedAt, userPriorities.collectionFoci]);

  // b18-profile-1 / b18-shundo-dex: focus-specific custom slot. pvp_top renders
  // PvpEventStrip; shundo renders ShundoTrophyPanel (real panel, b18-shundo-dex).
  // All other foci return undefined and fall through to the
  // priorityVariant/hundo fork inside HundoDexSection.
  const dexCustomSlot = useMemo(() => {
    if (userPriorities.questionnaireCompletedAt === null) return undefined;
    const top = userPriorities.collectionFoci[0];
    if (top === "pvp_top") {
      return (
        <PvpEventStrip leagueFoci={userPriorities.leagueFoci} />
      );
    }
    if (top === "shundo") {
      // b18-shundo-dex: real panel replaces the coming-soon placeholder.
      return <ShundoTrophyPanel />;
    }
    return undefined;
  }, [
    userPriorities.questionnaireCompletedAt,
    userPriorities.collectionFoci,
    userPriorities.leagueFoci,
  ]);

  return (
    <InfoBlockCollapseContext.Provider value={collapse}>
    <AppShell pageSpacing>
      {/* v1.3.4 b2: bumped pt-4 → pt-8 so the "Mirror your Pokedex." hero
          line has more breathing room below the AppShell header.
          v1.3.4 b1 left the page hugging the header on iPhone and the
          user explicitly asked for more top spacing above "Mirror your".
          b13i I2: bumped pt-8 → pt-12 for additional whitespace between the
          Nexus Dex header and Profile's first content row (the resume banner /
          hero), matching the Trade/Calendar top-spacing pass this build. */}
      <div className="relative pt-12 page-hero-top" data-testid="home-option-b">
        {/* b15c C4: top-level page heading "Your Profile. Your priorities." —
            two sentences (period after each), token-driven type (v2-display +
            --text-2xl), sits at the very top above the hero card and respects
            the b13i I2 pt-12 top-spacing so it never jams the status bar. The
            second sentence is tinted with the brand accent for the same
            "identity + accent" treatment as the Trade/Pokedex headers, and it
            colour-flips light/dark via the --accent token. `text-balance` lets
            it wrap to a stacked two lines on narrow iPhone widths while keeping
            "Your Profile." intact on the first line. */}
        <MenuHeading
          eyebrow="Profile"
          title="Your Plan."
          greenSuffix="Focused."
          className="pl-5"
          data-testid="profile-page-heading"
        />
        {/* Collapse all / expand all: one round icon button on the heading's
            row, top right. No words on it (owner ask, 2026-10-06: the worded
            pill covered the title on smaller screens); the doubled chevron
            shows what it WILL do, as SectionFoldAllButton does on My Pokémon.
            The label is for screen readers and the long-press tooltip. */}
        {blockKeys.size > 0 && (
          <button
            type="button"
            onClick={toggleAll}
            data-testid="profile-blocks-toggle-all"
            aria-label={anyOpen ? "Collapse all sections" : "Expand all sections"}
            title={anyOpen ? "Collapse all sections" : "Expand all sections"}
            className="absolute right-1 top-12 w-8 h-8 rounded-full grid place-items-center"
            style={{
              background: "var(--surface-raised)",
              border: "1px solid var(--border-subtle)",
              color: "var(--text-secondary)",
            }}
          >
            {anyOpen ? (
              <ChevronsDownUp className="w-4 h-4" aria-hidden />
            ) : (
              <ChevronsUpDown className="w-4 h-4" aria-hidden />
            )}
          </button>
        )}

        {/* Neon spine ────────────────────────────────────────────────────
            Single vertical bar with the progress dots attached via per-section
            offsets so the spine reads as a timeline of the home content.
            b13i I1: re-themed onto the inverse --rail-line token (the canonical
            inverse-theme rail), so the line is dark in light mode and light in
            dark mode, mirroring the shared DotRail on the other pages. */}
        <div
          aria-hidden
          /* w-px matches the shared DotRail line on the other pages (Profile
             was 2px); top-[-600px] runs the spine up behind the sticky header
             and off the top edge so it reads as a continuous timeline rather
             than starting with a hard cap below the header. */
          className="absolute left-1.5 top-[-600px] bottom-0 w-px rounded-full pointer-events-none"
          style={{ background: "var(--rail-line)", opacity: 0.4 }}
          data-testid="home-spine"
        />

        {/* Resume-scan banner stays above the fold so a returning trainer
            can pick up an in-flight scan immediately. Renders nothing when
            there's no in-flight session. */}
        <div className="pl-5 pt-1">
          <ResumeScanBanner />
        </div>

        {/* Hero row: compact eyebrow + title + start scan button. */}
        <SpineSection testId="section-hero" hero>
          <div
            className="surface-dark-translucent rounded-2xl px-4 py-3 relative overflow-hidden"
            style={{
              boxShadow: "0 8px 32px var(--accent-fill-glow-faint)",
            }}
          >
            <div
              aria-hidden
              className="absolute -top-6 -right-6 w-24 h-24 rounded-full pointer-events-none opacity-50"
              style={{ background: "var(--accent-subtle)" }}
            />
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] flex items-center gap-1.5"
               style={{ color: "var(--accent-fill)" }}>
              <span
                aria-hidden
                className="inline-block w-1.5 h-1.5 rounded-full animate-pulse"
                style={{ background: "var(--accent-fill)", boxShadow: "0 0 6px var(--accent-fill)" }}
              />
              Your Dex, Live
            </p>
            <div className="mt-1 flex items-center justify-between gap-3">
              <h1 className="text-[18px] font-extrabold leading-tight tracking-tight">
                Mirror your Pokedex.{" "}
                <span style={{ color: "var(--accent-fill)" }}>Plan with Nexus Dex.</span>
              </h1>
              <div className="shrink-0 flex items-center gap-1.5">
                {/* pr-ab1-header-refactor: the Settings gear moved into the
                    global header (AppShell) so it is reachable from every
                    screen, not just Profile. */}
                {/* PR-E11 fix 4: text colour routed through --text-on-accent
                    (the global "text on a brand-accent surface" token, white
                    in light theme / near-black in dark theme) so it stays
                    readable on either theme and stays in sync with every
                    other primary CTA in the app. The previous hardcoded #000
                    rendered as dark-on-dark-green on iOS where Safari was
                    sampling the accent token at full saturation. */}
                <button
                  onClick={startScan}
                  data-testid="button-home-start-scan"
                  className="inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-[11px] font-extrabold"
                  style={{
                    background: "var(--accent-fill)",
                    color: "var(--text-on-accent)",
                    boxShadow: "0 0 14px var(--accent-fill-glow)",
                  }}
                >
                  <span
                    aria-hidden
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: "color-mix(in srgb, var(--text-on-accent) 50%, transparent)" }}
                  />
                  Start Scan
                </button>
              </div>
            </div>
          </div>
        </SpineSection>

        {/* KPI strip — four compact chips in a single row. Each chip taps
            into /dex with a category filter so the existing routes are
            preserved. */}
        <SpineSection testId="section-kpis">
          <SectionLabel icon={Gauge} left="Progress" right="View Dex" rightHref="/dex" />
          <div className="grid grid-cols-4 gap-1.5" data-testid="kpi-strip">
            <KpiChip
              label="Main"
              value={mainCaught}
              pct={mainPct}
              color="var(--accent-fill)"
              href="/dex"
              testid="kpi-main"
            />
            <KpiChip
              label="Shiny"
              value={shinyCaught}
              pct={shinyPct}
              color="hsl(var(--shiny))"
              href="/dex?filter=shiny"
              testid="kpi-shiny"
            />
            <KpiChip
              label="Lucky"
              value={luckyCaught}
              pct={luckyPct}
              color="hsl(var(--lucky))"
              href="/dex?filter=lucky"
              testid="kpi-lucky"
            />
            <KpiChip
              label="Perfect"
              value={perfectCaught}
              pct={perfectPct}
              color="hsl(var(--perfect))"
              href="/dex?filter=hundo"
              testid="kpi-perfect"
            />
          </div>
        </SpineSection>

        {/* "Did you catch any of these?" — the events that finished this week,
            with what the trainer is still missing from each, as one-tap
            checkboxes. Directly under the KPI strip because it is the fastest
            way to move those numbers, and because a prompt about the weekend is
            worth nothing three screens down.

            RENDERS NOTHING when no recent event has an open gap, and takes no
            vertical space when it does not render. That condition is the whole
            reason it is allowed on the spine: the Next-up card was deleted from
            this page in July for presenting as a recommendation while carrying
            no signal, and an always-on "nothing to catch up on" card would be
            the same mistake with a different heading. */}
        {/* Shiny Hunt (2026-09-03; always-on at the TOP of the spine since
            2026-09-08, owner direction): there is always a shiny to hunt, so
            the block is a constant fixture, filled from the feeds (boosted
            events and stated boost windows, shiny debuts, raid bosses that
            can be shiny, the Rocket leaders' shadows). Above Quick-update
            because it is the first thing the owner wants to read. Own dot
            inside the InfoBlock, so a plain section with the spine gutter. */}
        {/* GO Wild Area (2026-10-19 owner ask): the week's card, above
            everything else on the spine while the events are on. Sunday 8 Nov
            6 pm local (Into the Wild from Monday) to Sunday 15 Nov 6 pm local;
            renders NOTHING outside that window, wrapper included, so the
            spine keeps its rhythm the other 51 weeks. Own dot inside the
            InfoBlock. */}
        <WildAreaCard className={`${BLOCK_GAP} pl-5 pr-1`} />

        <section className={`${BLOCK_GAP} pl-5 pr-1`} data-testid="section-shiny-hunt">
          <ShinyHuntBlock />
        </section>

        <EventCatchUpSection className={`${BLOCK_GAP} ml-5 mr-1`} />


        {/* The priorities radar MOVED TO SETTINGS (2026-08-19), under Account.
            It was a settings control living on a dashboard: nothing on it
            reported progress, it only showed what the trainer had told us and
            offered a button to change it. Profile keeps the things that move.

            The collapse preference went with it. Nothing else on this page
            used it, and a persisted collapse state for a section that no
            longer exists is a trap for whoever reads it next. */}


        {/* Next-up card REMOVED (2026-07-16, user direction): it was a
            BINARY on `hasImported` — once the trainer had ever imported a dex it
            read "Log new catches / Open Pokédex" forever, and said the same
            thing whether they had scanned nothing or nearly everything. It
            presented as a recommendation while carrying no signal, so it was
            noise on the spine. Scanning stays reachable from the hero's
            "Start Scan" CTA above (the same startScan handler), so nothing is
            stranded. If a next-action card returns it needs a real input —
            dex completion, priorities, or event state. */}

        {/* Recent activity strip REMOVED (2026-07-01, user direction): the
            silhouette strip + "Start your first scan" empty state were stale
            noise on the profile spine. The priority dex (HundoDexSection
            above) and Next-up card already surface actionable state, and scan
            history remains reachable from the dedicated route. */}

        {/* b13d S3: trainer-style widgets (raid radar, PvP season) driven by
            the questionnaire priorities. Renders nothing until the
            questionnaire is completed or if neither style applies. */}
        {/* Not a SpineSection: ProfilePriorityWidgets renders TWO blocks
            (event radar + PvP), and a single spine dot would mark only the
            first. Each block carries its own SectionHeadingDot instead, so both
            get the canonical dot.
            Framing fix (2026-07-16): the widgets used to live inside a
            SpineSection, which supplied the shared `pl-5 pr-1` gutter every
            other card on the spine uses. When PR e9e9488 swapped the spine
            wrapper for per-block SectionHeadingDots it dropped that gutter, so
            the Radar and PvP-season cards hung ~20px further left than every
            sibling card and their right edge overran the rail — the broken
            framing reported. Re-apply the canonical spine padding here (without
            the SpineSection's own dot, which would double-dot the first block)
            so both cards line up pixel-for-pixel with the rest of the spine. */}
        <section className={`${BLOCK_GAP} pl-5 pr-1`} data-testid="section-priority-widgets">
          <ProfilePriorityWidgets />
        </section>

        {/* Current raid bosses by tier (1★ / 3★ / 5★ / Mega), cross-referenced
            against the trainer's dex so the row reads as "these are the ones
            worth a pass". Shares the priority widgets' gutter for the same
            reason they do — the panel carries its own spine dot, so it must not
            sit inside a SpineSection (that would double-dot it) but it does need
            the canonical `pl-5 pr-1` framing. Renders nothing when the roster is
            empty or upstream is down. */}
        <section className={`${BLOCK_GAP} pl-5 pr-1`} data-testid="section-raid-bosses">
          <RaidBossPanel />
        </section>

        {/* Hundo Raid CPs (2026-09-04 owner ask): pick any boss in raids
            right now (event bosses included) and read its 15/15/15 catch CP,
            plain and weather boosted. Same gutter and own dot as the panel
            above; renders nothing without a roster. */}
        <section className={`${BLOCK_GAP} pl-5 pr-1`} data-testid="section-hundo-raid-cps">
          <HundoRaidCpBlock />
        </section>

        {/* Hundo trail (v1.1.3-b6 / PR #27 resolved). J7.5 carry-forward.
            b18-profile-1: gated by shouldRenderTrophySlot -- variants,
            gender_pair, and costumes foci hide the slot entirely.
            customSlot drives pvp_top -> PvpEventStrip and
            shundo -> ShundoTrophyPanel; other foci use the existing
            priorityVariant/hundo fork inside HundoDexSection.
            2026-06-27: moved ABOVE the Next-up block; 2026-09-04 (owner order):
            moved DOWN to sit just above PvP season, after the live blocks. */}
        {/* Always on (2026-09-04 owner direction): the trophy slot used to
            hide for the variants / gender-pair / costume foci; a focus with
            no dedicated panel now falls through to the Hundo dex like an
            unanswered questionnaire does. */}
        <SpineSection testId="section-hundo" dot={false}>
          <HundoDexSection
            title={dexSlotTitle}
            priorityVariant={dexSlotVariant}
            customSlot={dexCustomSlot}
          />
        </SpineSection>

        {/* Season + league tracker, restored 2026-08-07 (user direction). It
            used to render inside ProfilePriorityWidgets behind a `playsPvp`
            questionnaire check, so it vanished from the profile for anyone whose
            styles weren't PvP. It now sits directly under "Raids right now" and
            renders for everyone. Same gutter + own SpineDot as its neighbours
            (so NOT a SpineSection, which would double-dot it). */}
        {/* Rocket radar (2026-09-08 owner ask): Giovanni, the leaders and the
            grunt pool, each catchable shadow read against the Shiny Dex.
            Just above PvP season by direction; the Shiny Hunt block at the
            top links down to it by id. Same gutter and own dot as its
            neighbours. */}
        <section className={`${BLOCK_GAP} pl-5 pr-1`} data-testid="section-rocket-radar">
          <RocketRadarBlock />
        </section>

        <section className={`${BLOCK_GAP} pl-5 pr-1`} data-testid="section-pvp-season">
          <PvpSeasonPanel />
        </section>

        {/* Up next event (only when there's something to show). Single
            compact row keeps the viewport budget. */}
        {upcoming && (
          <SpineSection testId="section-upnext">
            <button
              onClick={() => {
                if (upcoming.id) armCalendarEvent(upcoming.id);
                setLocation("/calendar");
              }}
              data-testid="button-home-upnext"
              className="nx-box-soft w-full text-left rounded-2xl px-3.5 py-2.5 flex items-center gap-2.5 hover-elevate"
            >
              <CalendarIcon className="w-4 h-4 shrink-0" style={{ color: "var(--accent-fill)" }} />
              <span className="min-w-0 flex-1">
                <span className="block text-[9px] font-bold uppercase tracking-[0.16em] text-[var(--text-tertiary)]">
                  Up next, {upcoming.date}
                </span>
                <span className="block text-[12px] font-semibold truncate">
                  {upcoming.title}
                </span>
              </span>
              <ChevronRight className="w-4 h-4 shrink-0 text-[var(--text-tertiary)]" />
            </button>
          </SpineSection>
        )}

        {/* One credit for the whole spine (2026-10-20 owner direction): the
            Shiny Hunt, Raids, Rocket radar and Wild Area blocks used to each
            carry their own LeekDuck line. ScrapedDuck's terms want the source
            visible wherever its data renders; the page foot is that place. */}
        <p
          className={`${BLOCK_GAP} pl-5 pr-1 text-[10px] leading-relaxed text-[var(--text-tertiary)]`}
          data-testid="profile-attribution"
        >
          Event, raid and Rocket data from{" "}
          <a href="https://leekduck.com" target="_blank" rel="noreferrer" className="underline">
            LeekDuck.com
          </a>{" "}
          via{" "}
          <a href="https://github.com/bigfoott/ScrapedDuck" target="_blank" rel="noreferrer" className="underline">
            ScrapedDuck
          </a>
          . Hours are your local time.
        </p>
      </div>
    </AppShell>
    </InfoBlockCollapseContext.Provider>
  );
}

/**
 * The heading row for a block that is bare markup rather than an InfoBlock card
 * (the KPI strip). Same type as InfoBlock's h2 — 16px, semibold, uppercase,
 * 0.18em, adaptive ink — because it was an 8px tertiary micro-label sitting two
 * blocks above a 16px ink heading, and a page with two heading scales reads as
 * two pages.
 *
 * The collapsible variant went with the priorities radar (moved to Settings,
 * 2026-08-19). Nothing else on the page ever collapsed, so the branch, its three
 * props and the ChevronDown were dead weight pretending to be an option.
 */
function SectionLabel({
  icon: Icon, left, right, rightHref,
}: {
  icon?: LucideIcon;
  left: string;
  right?: string;
  rightHref?: string;
}) {
  const [, setLocation] = useLocation();
  return (
    <div className="flex items-center justify-between gap-3 mb-2">
      <div className="flex items-center gap-2 min-w-0">
        {Icon && <Icon className="w-4 h-4 shrink-0 text-[var(--accent-fill)]" aria-hidden />}
        <h2 className="text-base font-semibold uppercase tracking-[0.18em] text-[var(--text-primary)]">
          {left}
        </h2>
      </div>
      {right && rightHref && (
        // b13g G3: was an `<a href="#/dex">` fragment anchor — but the app
        // routes with wouter (path-based), so the hash did nothing useful and
        // the browser fragment jump shoved the scroll position to the top,
        // which read as the "scroll locks too far down" bug. Route properly.
        <button
          type="button"
          onClick={() => setLocation(rightHref)}
          data-testid="section-link"
          className="shrink-0 text-[9px] font-bold uppercase tracking-[0.16em] no-underline inline-flex items-center gap-0.5 text-[var(--brand-green-on-light)]"
        >
          {right}
          <ChevronRight className="w-2.5 h-2.5" />
        </button>
      )}
    </div>
  );
}

/**
 * The gutter wrapper every block on the spine sits in.
 *
 * ONE rhythm (BLOCK_GAP) and ONE gutter, because the page used to have two of
 * each: the hero, KPI strip and up-next card were 10px apart while the panels
 * below them were 38px (this wrapper's 10px plus an `mt-7` the panel components
 * carried themselves), so the same page had two different ideas of how far
 * apart two blocks are. The panels' own margins are gone; spacing is the page's
 * job now, and it is set in exactly one place.
 *
 * `dot` is off for blocks built from InfoBlock, which draw their own spine dot —
 * a wrapper dot as well would double-dot them.
 */
function SpineSection({
  children, testId, dot = true, hero = false,
}: { children: React.ReactNode; testId?: string; dot?: boolean;
  /** The first section under the page heading hugs it (mt-1) instead of
   *  taking the full block gap — the heading's line-height already supplies
   *  the air (2026-08-21 ask). The old `first:mt-3` never matched: the spine
   *  and resume-banner divs sit before the first section, so it isn't the
   *  :first-child. */
  hero?: boolean;
}) {
  return (
    <section
      className={`relative pl-5 pr-1 ${hero ? "mt-1" : BLOCK_GAP}`}
      data-testid={testId}
    >
      {/* b15c C2: the spine dot now reads the single canonical --dot-accent
          token (deep green in light, bright neon in dark) instead of the
          per-section hue drift (shiny/lucky/perfect/accent) it used before, so
          every dot on every page is the same colour. Glow matches DotRail and
          SectionHeadingDot. */}
      {dot && (
        <span
          aria-hidden
          className="absolute left-0.5 top-2 w-2 h-2 rounded-full"
          style={{ background: "var(--dot-accent)", boxShadow: "0 0 6px var(--dot-accent)" }}
        />
      )}
      {children}
    </section>
  );
}

function KpiChip({
  label, value, pct, color, href, testid,
}: {
  label: string;
  value: number;
  pct: number;
  color: string;
  href: string;
  testid: string;
}) {
  const [, setLocation] = useLocation();
  return (
    // b13g G3: see SectionLabel — converted from `<a href="#/dex">` to a wouter
    // route so the KPI chips navigate instead of triggering a fragment scroll
    // jump that fought the sticky-header offset.
    <button
      type="button"
      onClick={() => setLocation(href)}
      data-testid={testid}
      className="nx-box-soft rounded-xl px-2 py-2 text-center hover-elevate no-underline w-full block"
    >
      <div className="text-[7px] font-bold uppercase tracking-[0.12em]" style={{ color }}>
        {label}
      </div>
      <div className="text-[18px] font-extrabold leading-none mt-0.5 tabular-nums">
        {value.toLocaleString()}
      </div>
      <div
        className="h-[2px] mt-1 rounded-full overflow-hidden"
        style={{ background: "var(--border-subtle)" }}
      >
        <div
          style={{
            width: `${Math.max(2, pct)}%`,
            height: "100%",
            background: color,
            borderRadius: 2,
          }}
        />
      </div>
    </button>
  );
}
